import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { Product, ProductInput } from '../types';

export function useProducts() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => api.get<Product[]>('/products'),
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['products'] });
    queryClient.invalidateQueries({ queryKey: ['dishes'] });
    queryClient.invalidateQueries({ queryKey: ['today'] });
  }

  const createMutation = useMutation({
    mutationFn: (input: ProductInput) => api.post('/products', input),
    onSuccess: invalidate,
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: number; input: ProductInput }) => api.put(`/products/${id}`, input),
    onSuccess: invalidate,
  });
  const archiveMutation = useMutation({
    mutationFn: (id: number) => api.del(`/products/${id}`),
    onSuccess: invalidate,
  });

  return {
    products: data ?? [],
    loading: isLoading,
    createProduct: (input: ProductInput) => createMutation.mutateAsync(input),
    updateProduct: (id: number, input: ProductInput) => updateMutation.mutateAsync({ id, input }),
    archiveProduct: (id: number) => archiveMutation.mutateAsync(id),
    reload: () => queryClient.invalidateQueries({ queryKey: ['products'] }),
  };
}

export function useProduct(id: number | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: ['products', id],
    queryFn: () => api.get<Product>(`/products/${id}`),
    enabled: !!id,
  });

  return { product: data ?? null, loading: isLoading };
}
